import { Button } from '@/components/ui/button';
import Loader from '@/components/ui/Loader';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useStripeOnMount } from '@/provider/stripeLoader';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import {
  createSetupIntent,
  fetchPaymentMethods,
  fetchSubscription,
  fetchUsage,
  setPaymentMethodDefault,
} from '@/features/billing/billingThunks';
import { cn } from '@/lib/utils';
import { toast } from 'react-toastify';
import { fetchWorkspaceById } from '@/features/workspace/workspaceThunks';
import { formatPrice } from './usageExpiredUtils';

function StripeInputWithLabel({
  label,
  id,
  StripeElement,
  onCompleteChange,
}: {
  label: string;
  id: string;
  StripeElement: typeof CardNumberElement | typeof CardExpiryElement | typeof CardCvcElement;
  onCompleteChange?: (complete: boolean) => void;
}) {
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);
  const isActive = isFocused || hasValue;

  return (
    <div className={cn('relative px-4 py-2 h-14 rounded-lg border border-input bg-white')}>
      <label
        className={cn(
          'absolute transition-all duration-200 pointer-events-none text-tertiary',
          isActive ? 'text-sm top-0.5 left-3 px-1' : 'text-base top-1/2 left-4 -translate-y-1/2',
        )}
        htmlFor={id}
      >
        {label}
      </label>
      <div className="pt-4">
        <StripeElement
          id={id}
          options={{
            placeholder: '',
            style: {
              base: {
                color: '#09090a',
                fontSize: '16px',
                fontWeight: '400',
                padding: '8px 8px 8px 8px',
              },
            },
          }}
          onChange={(event) => {
            setHasValue(!event.empty);
            onCompleteChange?.(event.complete);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
      </div>
    </div>
  );
}

type PastDuePaymentFormProps = {
  paymentIntentClientSecret: string;
  amount: number;
  currency: string;
  workspaceId: number;
  disabled?: boolean;
  onSuccess: () => void;
  onBeforePayment?: () => Promise<boolean>;
};

export default function PastDuePaymentForm({
  paymentIntentClientSecret,
  amount,
  currency,
  workspaceId,
  disabled,
  onSuccess,
  onBeforePayment,
}: PastDuePaymentFormProps) {
  const { t } = useTranslation('billing');
  const dispatch = useAppDispatch();
  const stripe = useStripe();
  const elements = useElements();
  useStripeOnMount();
  const user = useAppSelector((s: RootState) => s.auth.user);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCardNumberComplete, setIsCardNumberComplete] = useState(false);
  const [isCardExpiryComplete, setIsCardExpiryComplete] = useState(false);
  const [isCardCvcComplete, setIsCardCvcComplete] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [isCardholderNameFocused, setIsCardholderNameFocused] = useState(false);

  const isFormValid = isCardNumberComplete && isCardExpiryComplete && isCardCvcComplete;
  const isCardholderNameActive = cardholderName.length > 0 || isCardholderNameFocused;

  const refreshAfterPayment = async () => {
    await dispatch(fetchSubscription(workspaceId)).unwrap();
    await Promise.all([
      dispatch(fetchUsage(workspaceId)),
      dispatch(fetchPaymentMethods(workspaceId)),
      dispatch(fetchWorkspaceById(workspaceId)),
    ]);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements || !paymentIntentClientSecret) return;

    if (onBeforePayment) {
      const ok = await onBeforePayment();
      if (!ok) return;
    }

    setIsProcessing(true);
    try {
      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) {
        toast.error(t('paymentForm.toast.formLoadError'));
        return;
      }

      const billingDetails = {
        name:
          cardholderName.trim() ||
          (user ? `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() : '') ||
          undefined,
        email: user?.email ?? undefined,
      };

      const setupResult = await dispatch(createSetupIntent(workspaceId)).unwrap();
      const { setupIntent, error: setupError } = await stripe.confirmCardSetup(
        setupResult.clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: billingDetails,
          },
        },
      );

      if (setupError) {
        toast.error(setupError.message ?? t('paymentForm.toast.saveCardError'));
        return;
      }

      const paymentMethodId =
        typeof setupIntent?.payment_method === 'string'
          ? setupIntent.payment_method
          : (setupIntent?.payment_method as { id?: string })?.id;

      if (!paymentMethodId) {
        toast.error(t('paymentForm.toast.noPaymentMethod'));
        return;
      }

      await dispatch(setPaymentMethodDefault({ workspaceId, paymentMethodId })).unwrap();

      const { error: paymentError } = await stripe.confirmCardPayment(paymentIntentClientSecret, {
        payment_method: paymentMethodId,
      });

      if (paymentError) {
        toast.error(paymentError.message ?? t('unpaid.toast.failed'));
        return;
      }

      await refreshAfterPayment();
      toast.success(t('unpaid.toast.success'));
      onSuccess();
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message;
      toast.error(message ?? t('unpaid.toast.error'));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <StripeInputWithLabel
        label={t('upgradePayment.cardNumber')}
        id="past-due-cardNumber"
        StripeElement={CardNumberElement}
        onCompleteChange={setIsCardNumberComplete}
      />

      <div className="flex gap-4">
        <div className="w-1/2">
          <StripeInputWithLabel
            label={t('upgradePayment.expiryDate')}
            id="past-due-cardExpiry"
            StripeElement={CardExpiryElement}
            onCompleteChange={setIsCardExpiryComplete}
          />
        </div>
        <div className="w-1/2">
          <StripeInputWithLabel
            label={t('upgradePayment.cvv')}
            id="past-due-cardCvc"
            StripeElement={CardCvcElement}
            onCompleteChange={setIsCardCvcComplete}
          />
        </div>
      </div>

      <div className={cn('relative px-4 py-2 h-14 rounded-lg border border-input bg-white')}>
        <label
          className={cn(
            'absolute transition-all duration-200 pointer-events-none text-tertiary',
            isCardholderNameActive
              ? 'text-sm top-0.5 left-3 px-1'
              : 'text-base top-1/2 left-4 -translate-y-1/2',
          )}
          htmlFor="past-due-cardholderName"
        >
          {t('upgradePayment.cardholderName')}
        </label>
        <input
          className="w-full text-base focus:outline-none focus:ring-0 focus:border-transparent pt-4 text-black"
          id="past-due-cardholderName"
          type="text"
          value={cardholderName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCardholderName(e.target.value)}
          onFocus={() => setIsCardholderNameFocused(true)}
          onBlur={() => setIsCardholderNameFocused(false)}
          required
        />
      </div>

      <Button
        type="submit"
        disabled={disabled || !stripe || isProcessing || !isFormValid}
        className="w-full"
      >
        {isProcessing && <Loader />}
        {isProcessing
          ? t('unpaid.paying')
          : t('unpaid.payAmount', { amount: formatPrice(amount, currency) })}
      </Button>
    </form>
  );
}
